"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  TURKISH_CASE_STRINGS,
  TurkishCaseToggle,
  useTurkishCase,
} from "@/components/tools/turkish-case-toggle";
import { writeToClipboard } from "@/lib/clipboard";
import {
  capitalizeFirst,
  foldLower,
  foldUpper,
  hasCase,
} from "@/lib/turkish-case";

/**
 * The case converter's interactive half: one textarea and ten live conversions.
 *
 * The ten result labels ARE the demonstration — "aLtErNaTiNg CaSe" is spelled that way
 * on purpose — so they sit in the strings object alongside the chrome labels rather than
 * in the content file, where a translator would have no way to tell which of them are
 * words to translate and which are specimens to leave alone.
 */

const RE_SPLIT_WITH_SPACES = /(\s+)/;
const RE_WHITESPACE_ONLY = /^\s+$/;

/*
 * Sentence starts and word splits by Unicode property, not by `\w` and `a-zA-Z0-9`.
 *
 * `\w` in JavaScript is `[A-Za-z0-9_]` even under the `u` flag, so `Sentence case` never
 * capitalised a sentence that did not open on an ASCII letter: "десять способов. это
 * второе." and "đây là câu. ứng dụng tốt." came back entirely lower-case, and German
 * "über allen gipfeln ist ruh. ölberg ist schön." was left alone at both sentence
 * starts. Only the first character of the sentence is matched and upper-cased, so this
 * class does not need `\p{M}`; the classes below, which have to hold a whole word
 * together, do.
 *
 * `RE_NON_ALNUM` was the worst of the three, because it produced nothing rather than
 * something wrong: it splits the text for camelCase, PascalCase, snake_case and
 * kebab-case, and every character outside ASCII was a separator, so FOUR of the ten
 * result cards were EMPTY for Russian, Arabic, Hindi, Korean, Chinese and Japanese and
 * shredded for the rest — "Grüße aus Köln" snake-cased to `gr_e_aus_k_ln` and
 * "Đây là tiêu đề" to `y_l_ti_u`. `\p{M}` belongs in it for the reason the
 * headline-analyzer records: Devanagari writes its vowels as combining marks, so without
 * it every matra is a word boundary and a four-word Hindi phrase splits into nine.
 */
const RE_SENTENCE_START = /(^\s*[\p{L}\p{N}_]|[.!?]\s+[\p{L}\p{N}_])/gu;
const RE_NON_ALNUM = /[^\p{L}\p{N}\p{M}]+/u;
/** An apostrophe inside a word ("it's", "O’Brien") belongs to the word, not between two. */
const RE_WORD_APOSTROPHE = /(?<=[\p{L}\p{N}\p{M}])['’ʼ](?=[\p{L}\p{N}\p{M}])/gu;

const MINOR_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "but",
  "or",
  "for",
  "nor",
  "on",
  "at",
  "to",
  "by",
  "in",
  "of",
  "up",
  "as",
  "is",
  "if",
  "it",
  "so",
  "yet",
  "via",
]);

/** English strings. A translated content file overrides these through `ui`. */
export const CASE_CONVERTER_STRINGS = {
  ...TURKISH_CASE_STRINGS,
  inputLabel: "Input Text",
  placeholder: "Type or paste your text here...",
  resultsHeading: "Results",
  copy: "Copy",
  copied: "Copied!",
  empty: "Type above to see output…",
  upper: "UPPERCASE",
  lower: "lowercase",
  title: "Title Case",
  sentence: "Sentence case",
  alternating: "aLtErNaTiNg CaSe",
  inverse: "InVeRsE CaSe (swap case)",
  camel: "camelCase",
  pascal: "PascalCase",
  snake: "snake_case",
  kebab: "kebab-case",
};

function capitalizeWord(word: string, turkish: boolean): string {
  return capitalizeFirst(word, turkish);
}

function isWordSegment(segment: string): boolean {
  return segment.length > 0 && !RE_WHITESPACE_ONLY.test(segment);
}

function toTitleCase(text: string, turkish: boolean): string {
  const segments = text.split(RE_SPLIT_WITH_SPACES);
  const wordIndices: number[] = [];
  for (let i = 0; i < segments.length; i++) {
    if (isWordSegment(segments[i])) {
      wordIndices.push(i);
    }
  }
  const first = wordIndices[0];
  const last = wordIndices.at(-1);
  return segments
    .map((segment, index) => {
      if (!isWordSegment(segment)) {
        return segment;
      }
      const word = foldLower(segment, turkish);
      if (index === first || index === last) {
        return capitalizeWord(word, turkish);
      }
      // `MINOR_WORDS` is an English list, matched against the locale-less fold on
      // purpose: Turkish folding turns "IN" into "ın", which is in no English table.
      if (MINOR_WORDS.has(segment.toLowerCase())) {
        return word;
      }
      return capitalizeWord(word, turkish);
    })
    .join("");
}

function toSentenceCase(text: string, turkish: boolean): string {
  return foldLower(text, turkish).replace(RE_SENTENCE_START, (match) =>
    foldUpper(match, turkish)
  );
}

/*
 * The two case-flipping conversions ask `hasCase`, not `\p{L}`.
 *
 * `[a-zA-Z]` skipped every accented letter, so alternation restarted around it and
 * produced "gRüße AuS köLn" — the `ü` and `ö` neither flipped nor advanced the index —
 * and did nothing whatever to "Десять способов писать". Widening it to `\p{L}` would
 * fix those and break the six unicameral scripts instead: folding a Hangul, Arabic,
 * Devanagari or Han character returns it unchanged, so the index would count characters
 * that never alternate and mixed text would come out mis-phased. `hasCase` asks the
 * question that actually matters — does this character change when you fold it.
 *
 * `.split("")` is left as it is: it splits astral pairs, but so does the code point
 * either way here, and the folds are per-character. `[...text]` would be the fix if a
 * script outside the BMP ever needed it.
 */
function toAlternatingCase(text: string, turkish: boolean): string {
  let i = 0;
  return text
    .split("")
    .map((char) => {
      if (hasCase(char)) {
        const result =
          i % 2 === 0 ? foldLower(char, turkish) : foldUpper(char, turkish);
        i++;
        return result;
      }
      return char;
    })
    .join("");
}

function toInverseCase(text: string, turkish: boolean): string {
  return text
    .split("")
    .map((char) => {
      if (!hasCase(char)) {
        return char;
      }
      if (char === foldLower(char, turkish)) {
        return foldUpper(char, turkish);
      }
      return foldLower(char, turkish);
    })
    .join("");
}

function splitWords(text: string, turkish: boolean): string[] {
  return text
    .replace(RE_WORD_APOSTROPHE, "")
    .split(RE_NON_ALNUM)
    .filter((word) => word.length > 0)
    .map((word) => foldLower(word, turkish));
}

export function toCamelCase(text: string, turkish: boolean): string {
  return splitWords(text, turkish)
    .map((word, index) => (index === 0 ? word : capitalizeWord(word, turkish)))
    .join("");
}

export function toPascalCase(text: string, turkish: boolean): string {
  return splitWords(text, turkish)
    .map((word) => capitalizeWord(word, turkish))
    .join("");
}

export function toSnakeCase(text: string, turkish: boolean): string {
  return splitWords(text, turkish).join("_");
}

export function toKebabCase(text: string, turkish: boolean): string {
  return splitWords(text, turkish).join("-");
}

function ResultCard({
  label,
  value,
  copy,
  copied: copiedLabel,
  empty,
}: {
  label: string;
  value: string;
  copy: string;
  copied: string;
  empty: string;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
          disabled={value.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? copiedLabel : copy}
        </button>
      </div>
      <p className="mt-2 min-h-6 whitespace-pre-wrap break-words text-foreground text-sm leading-relaxed">
        {value.length > 0 ? (
          value
        ) : (
          <span className="text-muted-foreground">{empty}</span>
        )}
      </p>
    </div>
  );
}

export default function CaseConverterWidget() {
  const strings = useToolUi(CASE_CONVERTER_STRINGS);
  const [input, setInput] = useState("");
  const [turkish, setTurkish] = useTurkishCase();

  const results: { label: string; value: string }[] = [
    { label: strings.upper, value: foldUpper(input, turkish) },
    { label: strings.lower, value: foldLower(input, turkish) },
    { label: strings.title, value: toTitleCase(input, turkish) },
    { label: strings.sentence, value: toSentenceCase(input, turkish) },
    { label: strings.alternating, value: toAlternatingCase(input, turkish) },
    { label: strings.inverse, value: toInverseCase(input, turkish) },
    { label: strings.camel, value: toCamelCase(input, turkish) },
    { label: strings.pascal, value: toPascalCase(input, turkish) },
    { label: strings.snake, value: toSnakeCase(input, turkish) },
    { label: strings.kebab, value: toKebabCase(input, turkish) },
  ];

  return (
    <>
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="case-input"
        >
          {strings.inputLabel}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="case-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
        <TurkishCaseToggle
          checked={turkish}
          onChange={setTurkish}
          strings={strings}
        />
      </div>

      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.resultsHeading}
        </h2>
        <div className="space-y-3">
          {results.map((result) => (
            <ResultCard
              copied={strings.copied}
              copy={strings.copy}
              empty={strings.empty}
              key={result.label}
              label={result.label}
              value={result.value}
            />
          ))}
        </div>
      </section>
    </>
  );
}
